import { useState, useEffect, useCallback } from 'react';
import { Swords, Link2, Clock, Flame, AlertTriangle, CheckCircle2, XCircle, Copy, Skull, Share2 } from 'lucide-react';
import * as pactService from '../../services/pactService';

const MILESTONE_7  = { days: 7,  badge: 'pact-warriors',  label: 'PACT WARRIORS' };
const MILESTONE_30 = { days: 30, badge: 'unbreakable',     label: 'UNBREAKABLE'  };

// ─── Sub-components ───────────────────────────────────────────────────────────

const ShameNotification = ({ shames, onDismiss }) => {
  if (!shames.length) return null;
  const latest = shames[0];
  return (
    <div
      className="bg-gradient-to-r from-red-900 to-red-800 border-2 border-red-500 p-3 mb-3 flex items-center justify-between"
      style={{ animation: 'shake 0.4s ease-in-out' }}
    >
      <div className="flex items-center gap-2">
        <span className="text-2xl">😤</span>
        <div>
          <p className="text-red-400 font-black text-sm uppercase tracking-wide">
            {latest.from_username} IS SHAMING YOU
          </p>
          <p className="text-red-300 text-xs font-bold">GET YOUR CHALLENGE DONE</p>
        </div>
      </div>
      <button
        onClick={onDismiss}
        className="text-red-400 hover:text-red-200 font-black text-lg px-2"
      >
        ✕
      </button>
    </div>
  );
};

const BitchOutNotification = ({ bitchOuts, partnerUsername, onDismiss }) => {
  if (!bitchOuts.length) return null;
  const latest = bitchOuts[0];
  const label = latest.reason === 'dissolved' ? 'DISSOLVED THE PACT' : 'MISSED THEIR CHALLENGE';
  return (
    <div className="bg-gradient-to-r from-orange-900 to-orange-800 border-2 border-orange-500 p-3 mb-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Skull size={20} className="text-orange-400 flex-shrink-0" />
        <div>
          <p className="text-orange-400 font-black text-sm uppercase tracking-wide">
            {partnerUsername} {label}
          </p>
          <p className="text-orange-300 text-xs font-bold">YOUR STREAK WAS BROKEN</p>
        </div>
      </div>
      <button onClick={onDismiss} className="text-orange-400 hover:text-orange-200 font-black text-lg px-2">✕</button>
    </div>
  );
};

const MilestoneBar = ({ streak }) => {
  const milestones = [MILESTONE_7, MILESTONE_30];
  const nextMilestone = milestones.find(m => streak < m.days);
  if (!nextMilestone) return (
    <div className="bg-gradient-to-r from-yellow-900 to-yellow-800 border border-yellow-600 p-2 text-center">
      <p className="text-yellow-400 font-black text-xs uppercase tracking-wide">
        🏆 FULLY UNBREAKABLE — ALL MILESTONES HIT
      </p>
    </div>
  );

  const pct = Math.min(100, (streak / nextMilestone.days) * 100);
  return (
    <div className="bg-black border border-gray-700 p-2">
      <div className="flex justify-between items-center mb-1">
        <span className="text-gray-400 text-xs font-black uppercase">NEXT: {nextMilestone.label}</span>
        <span className="text-gray-400 text-xs font-bold">{streak}/{nextMilestone.days} DAYS</span>
      </div>
      <div className="h-2 bg-gray-800 w-full">
        <div
          className="h-2 bg-gradient-to-r from-purple-600 to-purple-400 transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

// ─── NO PACT VIEW ─────────────────────────────────────────────────────────────

const NoPactView = ({ userId, username, onInviteSent, onInviteJoined, pendingPactCode }) => {
  const [inputVal, setInputVal]     = useState(pendingPactCode || '');
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [linkCode, setLinkCode]     = useState('');
  const [codeCopied, setCodeCopied] = useState(false);
  const [tab, setTab]               = useState(pendingPactCode ? 'code' : 'username');

  const handleSendInvite = async () => {
    const val = inputVal.trim();
    if (!val) { setError('Enter a username'); return; }
    if (val.toLowerCase() === username.toLowerCase()) { setError("Can't pact with yourself"); return; }

    setLoading(true);
    setError('');
    try {
      const target = await pactService.lookupUserByUsername(val);
      if (!target) { setError('User not found — check their username'); setLoading(false); return; }

      const invite = await pactService.createInvite({
        fromUserId: userId,
        fromUsername: username,
        toUserId: target.user_id,
        toUsername: target.username,
      });
      onInviteSent(invite);
    } catch (err) {
      setError('Failed to send invite. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLink = async () => {
    setLoading(true);
    setError('');
    try {
      const invite = await pactService.createInvite({
        fromUserId: userId,
        fromUsername: username,
      });
      setLinkCode(invite.invite_code);
    } catch {
      setError('Failed to generate link. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(linkCode).then(() => {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    });
  };

  const handleJoinByCode = async () => {
    const val = inputVal.trim().toUpperCase();
    if (val.length < 8 || val.length > 64) { setError('Enter a valid invite code'); return; }
    setLoading(true);
    setError('');
    try {
      const invite = await pactService.fetchInviteByCode(val);
      if (!invite) { setError('Code not found or expired'); setLoading(false); return; }
      if (invite.from_user_id === userId) { setError("That's your own invite"); setLoading(false); return; }
      const pact = await pactService.acceptInvite(invite.id);
      onInviteJoined(pact);
    } catch {
      setError('Failed to join. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-purple-500 p-4 sm:p-6">

      {/* Incoming invite banner */}
      {pendingPactCode && (
        <div className="bg-gradient-to-r from-purple-900 to-violet-900 border-2 border-purple-400 p-3 mb-4 flex items-center gap-3"
          style={{ boxShadow: '0 0 20px rgba(168,85,247,0.4)' }}
        >
          <span className="text-2xl flex-shrink-0">🔥</span>
          <p className="text-purple-200 font-black text-sm uppercase tracking-wide leading-snug">
            YOU'VE BEEN INVITED TO A PACT — Accept below to start your streak together
          </p>
        </div>
      )}

      {/* Header banner */}
      <div
        className="bg-gradient-to-r from-purple-600 to-violet-600 border-2 border-purple-400 p-3 sm:p-4 mb-4 skew-x-[-2deg]"
        style={{ boxShadow: '0 4px 24px rgba(168,85,247,0.4)' }}
      >
        <div className="skew-x-[2deg] flex items-center gap-3">
          <Swords size={28} className="text-white flex-shrink-0" />
          <div>
            <h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight leading-none">
              SEND IT PACT
            </h2>
            <p className="text-purple-200 text-xs sm:text-sm font-bold uppercase tracking-wider mt-0.5">
              COMMIT TOGETHER. FAIL TOGETHER.
            </p>
          </div>
        </div>
      </div>

      <p className="text-gray-400 text-sm font-bold uppercase tracking-wide mb-4 text-center">
        FORM A PACT — BOTH MUST SEND IT DAILY OR THE STREAK DIES
      </p>

      {/* Tab toggle */}
      <div className="grid grid-cols-2 gap-1 mb-4 border border-purple-700">
        <button
          onClick={() => { setTab('username'); setInputVal(''); setError(''); setLinkCode(''); }}
          className={`py-2.5 font-black text-xs uppercase tracking-wider transition-all ${
            tab === 'username'
              ? 'bg-purple-600 text-white'
              : 'bg-black text-purple-500 hover:bg-purple-900'
          }`}
        >
          BY USERNAME
        </button>
        <button
          onClick={() => { setTab('code'); setInputVal(''); setError(''); setLinkCode(''); }}
          className={`py-2.5 font-black text-xs uppercase tracking-wider transition-all ${
            tab === 'code'
              ? 'bg-purple-600 text-white'
              : 'bg-black text-purple-500 hover:bg-purple-900'
          }`}
        >
          BY INVITE CODE
        </button>
      </div>

      {tab === 'username' && (
        <div className="space-y-3">
          <div>
            <input
              type="text"
              value={inputVal}
              onChange={e => { setInputVal(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleSendInvite()}
              placeholder="ENTER THEIR USERNAME"
              maxLength={20}
              className="w-full bg-black border-2 border-purple-700 text-purple-300 placeholder-gray-600 p-3 font-black uppercase text-sm tracking-wide focus:outline-none focus:border-purple-400"
            />
          </div>
          <button
            onClick={handleSendInvite}
            disabled={loading}
            className="w-full bg-purple-600 hover:bg-purple-500 active:scale-95 text-white py-4 font-black text-base uppercase tracking-wider border-2 border-purple-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
          >
            {loading ? 'SENDING...' : 'SEND PACT INVITE'}
          </button>
        </div>
      )}

      {tab === 'code' && (
        <div className="space-y-3">
          {/* Generate a link */}
          {!linkCode ? (
            <button
              onClick={handleGenerateLink}
              disabled={loading}
              className="w-full bg-black border-2 border-purple-600 hover:border-purple-400 text-purple-400 hover:text-purple-300 py-3 font-black text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-all disabled:opacity-50 touch-manipulation"
            >
              <Link2 size={16} />
              {loading ? 'GENERATING...' : 'GENERATE MY INVITE CODE'}
            </button>
          ) : (
            <div className="bg-black border-2 border-purple-600 p-3">
              <p className="text-gray-500 text-xs font-bold uppercase mb-1">YOUR INVITE CODE</p>
              <div className="flex items-center justify-between gap-2">
                <span className="text-purple-300 font-black text-2xl tracking-widest">{linkCode}</span>
                <button
                  onClick={handleCopyCode}
                  className="flex items-center gap-1 bg-purple-700 hover:bg-purple-600 text-white px-3 py-2 font-black text-xs uppercase border border-purple-500 transition-all touch-manipulation"
                >
                  <Copy size={12} />
                  {codeCopied ? 'COPIED!' : 'COPY'}
                </button>
              </div>
              <p className="text-gray-600 text-xs font-bold mt-1.5">SHARE THIS WITH YOUR PARTNER — EXPIRES IN 7 DAYS</p>
            </div>
          )}

          <div className="flex items-center gap-2">
            <div className="flex-1 h-px bg-gray-700" />
            <span className="text-gray-600 text-xs font-bold uppercase">OR ENTER A CODE</span>
            <div className="flex-1 h-px bg-gray-700" />
          </div>

          {/* Join by code */}
          <input
            type="text"
            value={inputVal}
            onChange={e => { setInputVal(e.target.value.toUpperCase()); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleJoinByCode()}
            placeholder="ENTER INVITE CODE"
            maxLength={10}
            className="w-full bg-black border-2 border-purple-700 text-purple-300 placeholder-gray-600 p-3 font-black uppercase text-base tracking-widest text-center focus:outline-none focus:border-purple-400"
          />
          <button
            onClick={handleJoinByCode}
            disabled={loading}
            className="w-full bg-purple-600 hover:bg-purple-500 active:scale-95 text-white py-4 font-black text-base uppercase tracking-wider border-2 border-purple-400 transition-all disabled:opacity-50 touch-manipulation"
          >
            {loading ? 'JOINING...' : 'JOIN PACT'}
          </button>
        </div>
      )}

      {error && (
        <p className="mt-3 text-red-400 font-black text-xs uppercase tracking-wide text-center">{error}</p>
      )}

      {/* Rules callout */}
      <div className="mt-5 border border-gray-800 p-3 bg-gray-950">
        <p className="text-gray-500 text-xs font-black uppercase tracking-wide mb-2">PACT RULES</p>
        <ul className="space-y-1">
          {[
            'BOTH must complete daily challenges each day',
            'ONE miss resets the streak to zero',
            'SHAME button nudges your partner if they slack',
            '7 DAYS → PACT WARRIORS badge for both',
            '30 DAYS → UNBREAKABLE badge for both',
            'BITCH OUT = CARRIED badge for the one who didn\'t break it',
            'DISSOLVING counts as a bitch out for BOTH',
            'MAX 1 ACTIVE PACT AT A TIME',
          ].map((rule, i) => (
            <li key={i} className="text-gray-500 text-xs font-bold flex items-start gap-1.5">
              <span className="text-purple-600 mt-0.5 flex-shrink-0">▸</span>
              {rule}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ─── INVITE SENT VIEW ─────────────────────────────────────────────────────────

const InviteSentView = ({ invite, onCancel }) => {
  const [cancelling,   setCancelling]   = useState(false);
  const [linkCopied,   setLinkCopied]   = useState(false);
  const [shareCopied,  setShareCopied]  = useState(false);
  const [codeCopied,   setCodeCopied]   = useState(false);
  const canNativeShare = !!navigator.share;

  const inviteUrl = invite.invite_url || pactService.buildInviteUrl(invite.invite_code);

  const handleCancel = async () => {
    if (!window.confirm('Cancel this pact invite?')) return;
    setCancelling(true);
    try {
      await pactService.cancelInvite(invite.id);
      onCancel();
    } catch {
      alert('Failed to cancel. Try again.');
    } finally {
      setCancelling(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  const handleShare = () => {
    if (canNativeShare) {
      navigator.share({
        title: 'Send It Pact 🔥',
        text: "Stop overthinking. I'm challenging you to a Send It Pact — both of us complete daily challenges or the streak dies. You in?",
        url: inviteUrl,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(inviteUrl).then(() => {
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      });
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(invite.invite_code).then(() => {
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    });
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-purple-500 p-4 sm:p-6">
      <div className="text-center mb-6">
        <div
          className="inline-flex items-center justify-center w-20 h-20 border-2 border-purple-500 mb-4"
          style={{ boxShadow: '0 0 30px rgba(168,85,247,0.4)' }}
        >
          <Clock size={36} className="text-purple-400" style={{ animation: 'pulse 2s infinite' }} />
        </div>
        <h2 className="text-2xl font-black text-purple-400 uppercase tracking-tight">PACT PENDING</h2>
        <p className="text-gray-400 text-sm font-bold uppercase mt-1">
          WAITING FOR {invite.to_username ? invite.to_username.toUpperCase() : 'YOUR PARTNER'} TO ACCEPT
        </p>
      </div>

      {/* Primary share actions */}
      <div className="grid grid-cols-2 gap-2 mb-1">
        <button
          onClick={handleCopyLink}
          className="flex items-center justify-center gap-1.5 bg-purple-700 hover:bg-purple-600 active:scale-95 text-white py-3.5 font-black text-sm uppercase border-2 border-purple-500 transition-all touch-manipulation"
          style={{ boxShadow: '0 4px 16px rgba(168,85,247,0.4)' }}
        >
          <Copy size={14} />
          {linkCopied ? 'COPIED ✓' : 'COPY LINK'}
        </button>
        <button
          onClick={handleShare}
          className="flex items-center justify-center gap-1.5 bg-purple-600 hover:bg-purple-500 active:scale-95 text-white py-3.5 font-black text-sm uppercase border-2 border-purple-400 transition-all touch-manipulation"
          style={{ boxShadow: '0 4px 16px rgba(168,85,247,0.4)' }}
        >
          <Share2 size={14} />
          {shareCopied ? 'LINK COPIED ✓' : 'SHARE'}
        </button>
      </div>
      {!canNativeShare && (
        <p className="text-gray-600 text-xs font-bold uppercase text-center mb-2">
          On mobile, SHARE opens your native share sheet
        </p>
      )}

      {/* Code display + secondary copy */}
      <div className="bg-black border border-purple-800 p-3 mb-4">
        <p className="text-gray-600 text-xs font-bold uppercase mb-1.5">INVITE CODE (ALTERNATE)</p>
        <div className="flex items-center justify-between gap-2">
          <span className="text-purple-400 font-black text-2xl tracking-widest">{invite.invite_code}</span>
          <button
            onClick={handleCopyCode}
            className="flex items-center gap-1 bg-black hover:bg-purple-900 text-purple-500 hover:text-purple-300 px-3 py-1.5 font-black text-xs uppercase border border-purple-700 transition-all touch-manipulation"
          >
            <Copy size={10} />
            {codeCopied ? 'COPIED!' : 'COPY CODE'}
          </button>
        </div>
      </div>

      <p className="text-gray-600 text-xs font-bold uppercase text-center mb-5">
        EXPIRES IN 7 DAYS — SHARE THE LINK WITH YOUR PARTNER
      </p>

      <button
        onClick={handleCancel}
        disabled={cancelling}
        className="w-full bg-black border-2 border-gray-700 hover:border-red-700 text-gray-500 hover:text-red-400 py-3 font-black text-sm uppercase tracking-wider transition-all disabled:opacity-50 touch-manipulation"
      >
        {cancelling ? 'CANCELLING...' : 'CANCEL INVITE'}
      </button>
    </div>
  );
};

// ─── INVITE RECEIVED VIEW ─────────────────────────────────────────────────────

const InviteReceivedView = ({ invite, username, onAccepted, onDeclined, fromDeepLink }) => {
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    setLoading(true);
    try {
      const pact = await pactService.acceptInvite(invite.id);
      onAccepted(pact);
    } catch {
      alert('Failed to accept pact. Try again.');
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!window.confirm(`Decline ${invite.from_username}'s pact request?`)) return;
    setLoading(true);
    try {
      await pactService.declineInvite(invite.id);
      onDeclined();
    } catch {
      alert('Failed to decline. Try again.');
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-purple-500 p-4 sm:p-6">
      {fromDeepLink && (
        <div
          className="bg-gradient-to-r from-purple-900 to-violet-900 border-2 border-purple-400 p-3 mb-4 flex items-center gap-3"
          style={{ boxShadow: '0 0 20px rgba(168,85,247,0.4)' }}
        >
          <span className="text-2xl flex-shrink-0">🔥</span>
          <p className="text-purple-200 font-black text-sm uppercase tracking-wide leading-snug">
            YOU'VE BEEN INVITED TO A PACT — Accept below to start your streak together
          </p>
        </div>
      )}
      <div
        className="bg-gradient-to-r from-purple-600 to-violet-600 border-2 border-purple-400 p-4 mb-5 text-center"
        style={{ boxShadow: '0 4px 24px rgba(168,85,247,0.5)' }}
      >
        <Swords size={32} className="text-white mx-auto mb-2" />
        <h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight">PACT REQUEST</h2>
      </div>

      <div className="text-center mb-6">
        <p className="text-gray-300 text-lg font-black uppercase">
          <span className="text-purple-400">{invite.from_username.toUpperCase()}</span>
        </p>
        <p className="text-gray-400 font-bold uppercase text-sm mt-1">
          WANTS TO FORM A SEND IT PACT WITH YOU
        </p>
        <p className="text-gray-600 text-xs font-bold uppercase mt-3">
          BOTH MUST COMPLETE DAILY CHALLENGES EACH DAY.<br />MISS ONE AND THE STREAK DIES.
        </p>
      </div>

      <div className="space-y-3">
        <button
          onClick={handleAccept}
          disabled={loading}
          className="w-full bg-purple-600 hover:bg-purple-500 active:scale-95 text-white py-5 font-black text-xl uppercase tracking-wider border-2 border-purple-400 transition-all disabled:opacity-50 touch-manipulation"
          style={{ boxShadow: '0 4px 20px rgba(168,85,247,0.4)' }}
        >
          {loading ? 'FORMING PACT...' : '⚔️ ACCEPT PACT'}
        </button>
        <button
          onClick={handleDecline}
          disabled={loading}
          className="w-full bg-black border-2 border-gray-700 hover:border-gray-500 text-gray-500 hover:text-gray-400 py-3 font-black text-sm uppercase tracking-wider transition-all disabled:opacity-50 touch-manipulation"
        >
          DECLINE
        </button>
      </div>
    </div>
  );
};

// ─── ACTIVE PACT VIEW ─────────────────────────────────────────────────────────

const ActivePactView = ({
  pact, userId, username, dailyChallengeCompleted,
  onUnlockBadge, onPactDissolved, playSound, sendNotificationDirect,
}) => {
  const partnerId       = pact.user1_id === userId ? pact.user2_id : pact.user1_id;
  const partnerUsername = pact.user1_id === userId ? pact.user2_username : pact.user1_username;

  const [todayMine,    setTodayMine]    = useState(false);
  const [todayPartner, setTodayPartner] = useState(false);
  const [currentPact,  setCurrentPact]  = useState(pact);
  const [shameAlreadySent, setShameAlreadySent] = useState(false);
  const [shaming,      setShaming]      = useState(false);
  const [shameSentFeedback, setShameSentFeedback] = useState(false);
  const [shameCooldownRemaining, setShameCooldownRemaining] = useState(null); // { hours, minutes } | null

  const SHAME_COOLDOWN_KEY = `senditmaxx_last_shame_${partnerId}`;
  const SHAME_COOLDOWN_MS  = 60 * 60 * 1000; // 1 hour

  const checkShameCooldown = () => {
    const last = localStorage.getItem(SHAME_COOLDOWN_KEY);
    if (!last) return null;
    const elapsed = Date.now() - parseInt(last, 10);
    if (elapsed >= SHAME_COOLDOWN_MS) return null;
    const remaining = SHAME_COOLDOWN_MS - elapsed;
    const hours   = Math.floor(remaining / 3600000);
    const minutes = Math.ceil((remaining % 3600000) / 60000);
    return { hours, minutes };
  };
  const [dissolving,   setDissolving]   = useState(false);
  const [showDissolve, setShowDissolve] = useState(false);
  const [unseenShames, setUnseenShames] = useState([]);
  const [unseenBitchOuts, setUnseenBitchOuts] = useState([]);
  const [loading,      setLoading]      = useState(true);

  const loadPactData = useCallback(async () => {
    try {
      // Sync streak (handles increments + lapse detection)
      const { pact: synced, bitchOutUserId } = await pactService.syncPactStreak(currentPact, userId);
      setCurrentPact(synced);

      // Check milestone badges after streak sync
      if (synced.streak >= 30 && onUnlockBadge) {
        onUnlockBadge('unbreakable');
        onUnlockBadge('pact-warriors');
      } else if (synced.streak >= 7 && onUnlockBadge) {
        onUnlockBadge('pact-warriors');
      }

      // Award CARRIED badge to the user who didn't cause the bitch out
      if (bitchOutUserId && bitchOutUserId !== userId && onUnlockBadge) {
        onUnlockBadge('carried');
      }

      // Today completions
      const completed = await pactService.fetchTodayCompletions(synced.id);
      setTodayMine(completed.includes(userId));
      setTodayPartner(completed.includes(partnerId));

      // Shame check
      const alreadyShamed = await pactService.hasShamed(synced.id, userId);
      setShameAlreadySent(alreadyShamed);
      setShameCooldownRemaining(checkShameCooldown());

      // Unseen shames & bitch outs
      const [shames, bitchOuts] = await Promise.all([
        pactService.fetchUnseenShames(userId),
        pactService.fetchUnseenBitchOuts(userId),
      ]);
      setUnseenShames(shames);
      setUnseenBitchOuts(bitchOuts);
    } catch (err) {
      console.error('loadPactData error', err);
    } finally {
      setLoading(false);
    }
  }, [currentPact, userId, partnerId, onUnlockBadge]);

  // Initial load
  useEffect(() => { loadPactData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When daily challenge gets completed, record it
  useEffect(() => {
    if (dailyChallengeCompleted && !todayMine) {
      pactService.recordPactCompletion(currentPact.id, userId)
        .then(() => {
          setTodayMine(true);
          loadPactData();
        })
        .catch(err => console.error('recordPactCompletion error', err));
    }
  }, [dailyChallengeCompleted]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShame = async () => {
    const cooldown = checkShameCooldown();
    if (shameAlreadySent || todayPartner || shaming || cooldown) return;
    setShaming(true);
    try {
      await pactService.sendShame({
        pactId: currentPact.id,
        fromUserId: userId,
        fromUsername: username,
        toUserId: partnerId,
      });
      localStorage.setItem(SHAME_COOLDOWN_KEY, Date.now().toString());
      setShameAlreadySent(true);
      setShameCooldownRemaining(checkShameCooldown());
      setShameSentFeedback(true);
      setTimeout(() => setShameSentFeedback(false), 3000);
      if (playSound) playSound('click');
      if (sendNotificationDirect) {
        sendNotificationDirect(
          '😤 SHAME SENT',
          `You shamed ${partnerUsername} — they'll see it when they open the app`
        );
      }
    } catch (err) {
      console.error('sendShame error', err);
      alert('Failed to send shame. Try again.');
    } finally {
      setShaming(false);
    }
  };

  const handleDissolve = async () => {
    if (dissolving) return;
    setDissolving(true);
    try {
      await pactService.dissolvePact(currentPact, userId);
      if (playSound) playSound('bitchout');
      onPactDissolved();
    } catch {
      alert('Failed to dissolve pact. Try again.');
      setDissolving(false);
    }
  };

  const dismissShames = async () => {
    await pactService.markShamesSeen(userId);
    setUnseenShames([]);
  };

  const dismissBitchOuts = async () => {
    await pactService.markBitchOutsSeen(userId);
    setUnseenBitchOuts([]);
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-purple-500 p-8 text-center">
        <p className="text-purple-400 font-black text-lg uppercase tracking-wide animate-pulse">LOADING PACT...</p>
      </div>
    );
  }

  const streak = currentPact.streak || 0;

  return (
    <div className="space-y-3">

      {/* Shame / Bitch-out notifications */}
      <ShameNotification shames={unseenShames} onDismiss={dismissShames} />
      <BitchOutNotification bitchOuts={unseenBitchOuts} partnerUsername={partnerUsername} onDismiss={dismissBitchOuts} />

      {/* Pact Header */}
      <div
        className="bg-gradient-to-r from-purple-700 to-violet-700 border-2 border-purple-400 p-4 sm:p-5 skew-x-[-2deg]"
        style={{ boxShadow: '0 4px 30px rgba(168,85,247,0.5)' }}
      >
        <div className="skew-x-[2deg] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Swords size={24} className="text-white flex-shrink-0" />
            <div>
              <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight leading-none">
                SEND IT PACT
              </h2>
              <p className="text-purple-200 text-xs font-bold uppercase mt-0.5">
                YOU & {partnerUsername.toUpperCase()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Streak Counter */}
      <div
        className="bg-gradient-to-br from-gray-900 to-black border-2 border-purple-500 p-5 text-center"
        style={{ boxShadow: streak > 0 ? '0 0 40px rgba(168,85,247,0.3)' : 'none' }}
      >
        <p className="text-gray-500 text-xs font-black uppercase tracking-widest mb-1">PACT STREAK</p>
        <div className="flex items-center justify-center gap-2">
          <Flame size={32} className={streak > 0 ? 'text-purple-400' : 'text-gray-700'} />
          <span
            className="font-black leading-none"
            style={{
              fontSize: streak >= 100 ? '4rem' : '5rem',
              color: streak > 0 ? '#a855f7' : '#374151',
              textShadow: streak > 0 ? '0 0 30px rgba(168,85,247,0.8)' : 'none',
            }}
          >
            {streak}
          </span>
          <Flame size={32} className={streak > 0 ? 'text-purple-400' : 'text-gray-700'} />
        </div>
        <p className="text-gray-500 text-xs font-black uppercase tracking-widest mt-1">
          {streak === 0 ? 'NO STREAK — GET MOVING' : streak === 1 ? 'DAY' : 'DAYS'}
        </p>
      </div>

      {/* Today's Status — both users */}
      <div className="grid grid-cols-2 gap-3">
        {/* My Status */}
        <div
          className={`border-2 p-4 text-center ${
            todayMine
              ? 'bg-gradient-to-br from-green-900 to-black border-green-500'
              : 'bg-gradient-to-br from-gray-900 to-black border-gray-700'
          }`}
        >
          <p className="text-gray-500 text-xs font-black uppercase tracking-wide mb-2">YOU</p>
          {todayMine ? (
            <>
              <CheckCircle2 size={32} className="text-green-400 mx-auto mb-1" />
              <p className="text-green-400 font-black text-sm uppercase">SENT IT</p>
            </>
          ) : (
            <>
              <XCircle size={32} className="text-gray-700 mx-auto mb-1" />
              <p className="text-gray-600 font-black text-sm uppercase">NOT YET</p>
            </>
          )}
        </div>

        {/* Partner Status */}
        <div
          className={`border-2 p-4 text-center ${
            todayPartner
              ? 'bg-gradient-to-br from-green-900 to-black border-green-500'
              : 'bg-gradient-to-br from-gray-900 to-black border-gray-700'
          }`}
        >
          <p className="text-gray-500 text-xs font-black uppercase tracking-wide mb-2">
            {partnerUsername.toUpperCase()}
          </p>
          {todayPartner ? (
            <>
              <CheckCircle2 size={32} className="text-green-400 mx-auto mb-1" />
              <p className="text-green-400 font-black text-sm uppercase">SENT IT</p>
            </>
          ) : (
            <>
              <XCircle size={32} className="text-gray-700 mx-auto mb-1" />
              <p className="text-gray-600 font-black text-sm uppercase">NOT YET</p>
            </>
          )}
        </div>
      </div>

      {/* Both done today — celebrate */}
      {todayMine && todayPartner && (
        <div
          className="bg-gradient-to-r from-green-900 to-emerald-900 border-2 border-green-500 p-3 text-center"
          style={{ animation: 'scoreGlow 2s ease-in-out infinite' }}
        >
          <p className="text-green-400 font-black uppercase tracking-wide">
            ✓ BOTH SENT IT TODAY — STREAK LIVES
          </p>
        </div>
      )}

      {/* SHAME button */}
      {!todayPartner && (
        <button
          onClick={handleShame}
          disabled={shameAlreadySent || shaming || todayPartner || !!shameCooldownRemaining}
          className={`w-full py-5 font-black text-xl uppercase tracking-wider border-2 transition-all active:scale-95 touch-manipulation ${
            shameSentFeedback
              ? 'bg-green-900 border-green-600 text-green-400 cursor-not-allowed'
              : shameAlreadySent || shameCooldownRemaining
              ? 'bg-gray-900 border-gray-700 text-gray-600 cursor-not-allowed'
              : 'bg-red-700 hover:bg-red-600 border-red-500 text-white'
          }`}
          style={!shameAlreadySent && !shameCooldownRemaining ? { boxShadow: '0 4px 20px rgba(239,68,68,0.5)' } : {}}
        >
          {shaming
            ? 'SHAMING...'
            : shameSentFeedback
            ? "✓ SHAMED — They've been notified"
            : shameCooldownRemaining
            ? `SHAME SENT RECENTLY — WAIT ${shameCooldownRemaining.hours}H ${shameCooldownRemaining.minutes}M`
            : shameAlreadySent
            ? '😤 SHAME SENT — WAIT TILL TOMORROW'
            : '😤 SHAME THEM'}
        </button>
      )}

      {/* Milestone progress */}
      <MilestoneBar streak={streak} />

      {/* Pact stats */}
      <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 p-3">
        <p className="text-gray-600 text-xs font-black uppercase tracking-wide mb-2">PACT INFO</p>
        <div className="space-y-1">
          <div className="flex justify-between">
            <span className="text-gray-600 text-xs font-bold uppercase">FORMED</span>
            <span className="text-gray-400 text-xs font-bold">
              {new Date(currentPact.created_at).toLocaleDateString()}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 text-xs font-bold uppercase">PARTNER</span>
            <span className="text-purple-400 text-xs font-black">{partnerUsername.toUpperCase()}</span>
          </div>
          {streak >= 7 && (
            <div className="flex justify-between">
              <span className="text-gray-600 text-xs font-bold uppercase">STATUS</span>
              <span className="text-yellow-400 text-xs font-black">
                {streak >= 30 ? '🏆 UNBREAKABLE' : '⚔️ PACT WARRIORS'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Dissolve Pact */}
      {!showDissolve ? (
        <button
          onClick={() => setShowDissolve(true)}
          className="w-full bg-black border border-gray-800 hover:border-red-900 text-gray-700 hover:text-red-700 py-3 font-black text-xs uppercase tracking-wider transition-all touch-manipulation"
        >
          DISSOLVE PACT
        </button>
      ) : (
        <div className="border-2 border-red-800 bg-red-950 p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} className="text-red-500 flex-shrink-0" />
            <p className="text-red-400 font-black text-sm uppercase tracking-wide">
              THIS IS A BITCH OUT
            </p>
          </div>
          <p className="text-red-300 text-xs font-bold mb-1">
            Dissolving the pact counts as a bitch out for <span className="text-white">BOTH</span> of you.
            Your streak of <span className="text-red-400 font-black">{streak} days</span> will be gone.
          </p>
          {streak >= 7 && (
            <p className="text-orange-400 text-xs font-black uppercase mb-3">
              ⚠️ YOUR PARTNER WILL EARN THE CARRIED BADGE
            </p>
          )}
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button
              onClick={() => setShowDissolve(false)}
              className="bg-black border border-gray-700 text-gray-400 py-3 font-black text-xs uppercase tracking-wide touch-manipulation"
            >
              KEEP IT
            </button>
            <button
              onClick={handleDissolve}
              disabled={dissolving}
              className="bg-red-700 hover:bg-red-600 border border-red-500 text-white py-3 font-black text-xs uppercase tracking-wide transition-all disabled:opacity-50 touch-manipulation"
            >
              {dissolving ? 'DISSOLVING...' : 'BITCH OUT'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── MAIN PACT VIEW ───────────────────────────────────────────────────────────

export const PactView = ({
  userId, username, dailyChallengeCompleted,
  onUnlockBadge, playSound, sendNotificationDirect,
  pendingPactCode,
}) => {
  const [state, setState] = useState('loading');
  // state: 'loading' | 'no_pact' | 'invite_sent' | 'invite_received' | 'active'

  const [activePact,       setActivePact]       = useState(null);
  const [sentInvite,       setSentInvite]       = useState(null);
  const [receivedInvite,   setReceivedInvite]   = useState(null);
  const [fromDeepLink,     setFromDeepLink]     = useState(false);

  const loadState = useCallback(async () => {
    if (!userId) return;
    setState('loading');
    try {
      // 1. Check for active pact
      const pact = await pactService.fetchActivePact(userId);
      if (pact) {
        setActivePact(pact);
        setState('active');
        return;
      }

      // 2. Check for pending invites
      const invites = await pactService.fetchMyPendingInvites(userId);
      const received = invites.find(i => i.to_user_id === userId || (i.to_user_id === null && i.from_user_id !== userId));
      const sent     = invites.find(i => i.from_user_id === userId);

      if (received) {
        setReceivedInvite(received);
        setState('invite_received');
      } else if (sent) {
        setSentInvite(sent);
        setState('invite_sent');
      } else {
        setState('no_pact');
      }
    } catch (err) {
      console.error('PactView loadState error', err);
      setState('no_pact');
    }
  }, [userId]);

  useEffect(() => { loadState(); }, [loadState]);

  // Auto-resolve pending pact code passed in from URL param (handled by App.js)
  useEffect(() => {
    if (!pendingPactCode || state !== 'no_pact' || !userId) return;
    pactService.fetchInviteByCode(pendingPactCode).then(invite => {
      if (invite && invite.from_user_id !== userId) {
        setReceivedInvite(invite);
        setFromDeepLink(true);
        setState('invite_received');
      }
    }).catch(() => {});
  }, [pendingPactCode, state, userId]);

  if (state === 'loading') {
    return (
      <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-purple-500 p-12 text-center">
        <Swords size={32} className="text-purple-600 mx-auto mb-3 animate-pulse" />
        <p className="text-purple-400 font-black text-lg uppercase tracking-wide animate-pulse">LOADING PACT...</p>
      </div>
    );
  }

  if (state === 'no_pact') {
    return (
      <NoPactView
        userId={userId}
        username={username}
        onInviteSent={invite => { setSentInvite(invite); setState('invite_sent'); }}
        onInviteJoined={pact => { setActivePact(pact); setState('active'); }}
        pendingPactCode={pendingPactCode}
      />
    );
  }

  if (state === 'invite_sent') {
    return (
      <InviteSentView
        invite={sentInvite}
        onCancel={() => { setSentInvite(null); setState('no_pact'); }}
      />
    );
  }

  if (state === 'invite_received') {
    return (
      <InviteReceivedView
        invite={receivedInvite}
        username={username}
        onAccepted={pact => { setActivePact(pact); setState('active'); if (playSound) playSound('success'); }}
        onDeclined={() => { setReceivedInvite(null); setFromDeepLink(false); setState('no_pact'); }}
        fromDeepLink={fromDeepLink}
      />
    );
  }

  if (state === 'active') {
    return (
      <ActivePactView
        pact={activePact}
        userId={userId}
        username={username}
        dailyChallengeCompleted={dailyChallengeCompleted}
        onUnlockBadge={onUnlockBadge}
        playSound={playSound}
        sendNotificationDirect={sendNotificationDirect}
        onPactDissolved={() => {
          setActivePact(null);
          setState('no_pact');
        }}
      />
    );
  }

  return null;
};
